import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, BadgeCheck, GitMerge, ShieldCheck, Users } from 'lucide-react'
import { fetchExecutiveOverview, type ExecutiveOverview as Overview } from '../lib/api'
import {
  CoverageNotice,
  ErrorState,
  LoadingState,
  Meter,
  RankedBars,
  StatTile,
  UnavailableNotice,
  humanize,
} from './DataViz'

/**
 * Executive overview for the /app home (AFOS-41): pending human gates lead,
 * then the case pipeline, engineering throughput, verified outcomes, squads,
 * and budget usage. Every number comes from /api/afos360/v1/overview; a source
 * the server could not read is named, never shown as zero.
 */
export function ExecutiveOverview({ refreshKey = 0 }: { refreshKey?: number }) {
  const [data, setData] = useState<Overview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const latest = useRef(0)

  async function load() {
    const mine = ++latest.current
    setLoading(true)
    setError(null)
    try {
      const next = await fetchExecutiveOverview()
      if (mine === latest.current) setData(next)
    } catch (err) {
      if (mine === latest.current) setError(err instanceof Error ? err.message : 'Failed to load the overview')
    } finally {
      if (mine === latest.current) setLoading(false)
    }
  }

  // Reloads on mount and whenever the host page syncs (refreshKey changes).
  useEffect(() => {
    void load()
  }, [refreshKey])

  if (loading && !data) return <LoadingState label="Loading the executive overview" />
  if (error && !data) {
    const unscoped = /scope/i.test(error)
    return (
      <ErrorState
        message={
          unscoped
            ? 'Pick a customer and project to see the executive overview.'
            : error
        }
        onRetry={() => void load()}
      />
    )
  }
  if (!data) return null

  const gates = data.gates
  const cases = data.cases
  const runs = data.runs
  const verified = cases?.outcomes?.resolved ?? null
  const gateBars = Object.entries(gates.by_kind).map(([kind, count]) => ({
    key: kind,
    label: gates.kinds[kind]?.label ?? humanize(kind),
    value: count,
    href: '/approvals',
  }))

  return (
    <section aria-labelledby="exec-overview" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="exec-overview" className="font-[family-name:var(--font-display)] text-2xl font-extrabold tracking-tight">
            Executive overview
          </h2>
          <p className="text-sm text-[var(--color-mist)]">
            Live from this project's ledgers · updated{' '}
            {new Date(data.generated_at).toLocaleTimeString()}
          </p>
        </div>
        <Link
          to="/approvals"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--color-amber)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
        >
          Open approvals <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      </div>

      {error && (
        <ErrorState
          message={`Refresh failed; showing the snapshot from ${new Date(data.generated_at).toLocaleTimeString()}. ${error}`}
          onRetry={() => void load()}
        />
      )}
      <UnavailableNotice sources={data.unavailable} />
      <CoverageNotice incomplete={data.incomplete} restricted={data.restricted} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          emphasis
          label="Pending human gates"
          value={gates.total}
          hint={gates.total ? 'Agents are waiting on a named person.' : 'Nothing is waiting on a person.'}
          icon={<ShieldCheck aria-hidden="true" className="h-5 w-5 text-[var(--color-amber)]" />}
        />
        <StatTile
          label="Support cases"
          value={cases ? cases.total : '—'}
          hint={cases ? `${cases.by_state.length} states in flight` : data.restricted.cases ? 'Needs support access' : 'Unavailable'}
          icon={<BadgeCheck aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
        />
        <StatTile
          label="Engineering runs · 7 days"
          value={runs ? runs.started_7d : '—'}
          hint={runs ? `${runs.merge_approved} merge-approved of ${runs.total} total` : 'Unavailable'}
          icon={<GitMerge aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
        />
        <StatTile
          label="Reviewed resolutions"
          value={verified ?? '—'}
          hint={
            cases
              ? cases.stale_outcomes
                ? `${cases.stale_outcomes} outcome(s) retracted for re-review`
                : 'Reviewed by a person independent of the decision'
              : data.restricted.cases
                ? 'Needs support access'
                : 'Unavailable'
          }
          icon={<Users aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <RankedBars
          title="Waiting on a person"
          tone="amber"
          data={gateBars}
          empty="No pending gates. New handoff decisions, merge approvals, outcome reviews, and skill reviews appear here."
        />
        <RankedBars
          title="Support cases by state"
          data={(cases?.by_state ?? []).map((r) => ({
            key: r.state,
            label: humanize(r.state),
            value: r.count,
            href: '/cases',
          }))}
          empty={
            cases ? (
              <>
                No cases yet. Cases arrive through support intake (Support Desk packets or Linear).
              </>
            ) : data.restricted.cases ? (
              'Case data needs support access.'
            ) : (
              'Case ledger unavailable.'
            )
          }
        />
        <RankedBars
          title="Engineering runs by status"
          data={(runs?.by_status ?? []).map((r) => ({
            key: r.status,
            label: humanize(r.status),
            value: r.count,
            href: '/tickets',
          }))}
          empty={runs ? 'No engineering runs yet. Start one from Tickets or a case handoff.' : 'Run ledger unavailable.'}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Meter
          label="Concurrent runs"
          used={data.budgets?.usage ? data.budgets.usage.concurrent.used : null}
          limit={data.budgets?.usage?.concurrent.limit}
          hint={
            !data.budgets
              ? 'Budget policy could not be read.'
              : !data.budgets.usage
                ? 'Run ledger not fully read; usage unknown.'
                : `Budget source: ${data.budgets.source ?? 'platform'}`
          }
        />
        <Meter
          label="Runs started · 24 hours"
          used={data.budgets?.usage ? data.budgets.usage.per_day.used : null}
          limit={data.budgets?.usage?.per_day.limit}
          hint={!data.budgets || !data.budgets.usage ? 'Usage unknown; not shown as zero.' : undefined}
        />
        <StatTile
          label="Squads"
          value={data.squads ? data.squads.count : '—'}
          hint={
            <Link to="/squads" className="text-[var(--color-cyan)] underline-offset-2 hover:underline">
              Open the squad board
            </Link>
          }
        />
      </div>
    </section>
  )
}
